import { Route, Switch, useLocation, matchPath, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { lazy, useEffect } from 'react';
import NotAccessPage from '../components/common/NotAccessPage';
import { ROUTES, paths, publicRoutes } from '../constants';
import ErrorPage from '../components/common/ErrorPage';
import { fetchCustomerInfo } from '../api/CustomerApi';
import NotFound from '../components/common/NotFound';
import Loadable from '../components/common/Loadable';
import { SET_PROFILE } from '../redux/_user.redux';
import { checkContract } from '../api/ContractApi';
import { useToast } from '../hooks/ToastProvider';
import { checkResetToken } from '../api/AuthApi';
import { JwtHelper } from '../helpers/jwt';
import { saveReturnPath } from '../helpers/routeHistory';
import MainLayout from './../layout/MainLayout';
import ElectricRoutes from './ElectricRoutes';

const LoginDefault = Loadable(lazy(() => import('../views/login')));
const ContractDefault = Loadable(lazy(() => import('../views/contract')));
const CarDefault = Loadable(lazy(() => import('../views/car')));
const MemberDefault = Loadable(lazy(() => import('../views/member')));
const ForgetPasswordDefault = Loadable(lazy(() => import('../views/forgetPassword')));
const ResetPasswordDefault = Loadable(lazy(() => import('../views/resetPassword')));
const ChangePasswordDefault = Loadable(lazy(() => import('../views/changePassword')));
const SetPasswordDefault = Loadable(lazy(() => import('../views/setPassword')));
const SetPasswordByTokenDefault = Loadable(lazy(() => import('../views/setPasswordByToken')));
const PasswordSuccessDefault = Loadable(lazy(() => import('../views/passwordSuccess')));
const ProfileDefault = Loadable(lazy(() => import('../views/profile')));
const ContractDetailDefault = Loadable(lazy(() => import('../views/contract/[id]')));
const TodayPriceDefault = Loadable(lazy(() => import('../views/todayPrice')));
const ForgetPasswordSentDefault = Loadable(lazy(() => import('../views/forgetPassword/forgetPasswordSent')));
const ChargingBillDefault = Loadable(lazy(() => import('../views/chargingBill')));
const RegisterCardDefault = Loadable(lazy(() => import('../views/registerCard')));
const RegisterCardSuccessDefault = Loadable(lazy(() => import('../views/registerCardSuccess')));
const RegisterCardFailureDefault = Loadable(lazy(() => import('../views/registerCardFailure')));
const CreditCardDefault = Loadable(lazy(() => import('../views/creditCard')));
const EditInfoDefault = Loadable(lazy(() => import('../views/editInfo')));
const EditEmailDefault = Loadable(lazy(() => import('../views/editEmail')));
const EditPhoneDefault = Loadable(lazy(() => import('../views/editPhone')));
const DocumentDefault = Loadable(lazy(() => import('../views/document')));

//-----------------------|| MAIN ROUTING ||-----------------------//

const MainRoutes = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const profile = useSelector((state) => state.user.profile);
    const currentPath = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const contractId = searchParams.get('contractId');
    const token = searchParams.get('token');
    const resetToken = searchParams.get('reset_token');
    const accountId = searchParams.get('sfid');
    const hasViewAccess = currentPath === ROUTES.CAR;
    const { showToast } = useToast();

    const {
        data: userContractRole,
        isLoading: isLoadingCheckContract,
        error: checkContractError
    } = useQuery(['checkContract', contractId], () => checkContract(contractId, hasViewAccess), {
        enabled: (!!contractId && paths.includes(currentPath) && currentPath === ROUTES.CAR) || currentPath === ROUTES.MEMBER
    });
    const { isLoading: isLoadingCheckResetToken, error: checkResetTokenError } = useQuery(
        ['checkResetToken', resetToken],
        () => checkResetToken(resetToken),
        {
            enabled: !!resetToken && currentPath === ROUTES.RESET_PASSWORD
        }
    );

    if (token) {
        localStorage.setItem('token', token);
    }

    // Check if token exists and is valid
    const tokenFromStorage = localStorage.getItem('token') || localStorage.getItem('auth.token');
    const isTokenValid = tokenFromStorage && JwtHelper.isValid(tokenFromStorage);
    const isUserLoggedIn = Boolean(isTokenValid);
    
    // If token exists but is invalid, clear it and save return path immediately
    // (don't wait for useEffect to avoid race conditions with redirect)
    if (tokenFromStorage && !isTokenValid && !publicRoutes.includes(currentPath) && !localStorage.getItem('returnPath')) {
        saveReturnPath();
    }
    
    // Clean up invalid tokens
    useEffect(() => {
        if (tokenFromStorage && !isTokenValid && !publicRoutes.includes(currentPath)) {
            localStorage.removeItem('token');
            localStorage.removeItem('auth.token');
            localStorage.removeItem('auth.refreshToken');
            localStorage.removeItem('auth.credential');
        }
    }, [tokenFromStorage, isTokenValid, currentPath]);

    const isChangePasswordNotLoggedIn = currentPath === ROUTES.CHANGE_PASSWORD && !!accountId;
    const isNotPublicPage =
        (!publicRoutes.includes(currentPath) && isUserLoggedIn && !checkContractError && !isChangePasswordNotLoggedIn) ||
        (currentPath === ROUTES.CHANGE_PASSWORD && !currentPath.includes('?'));
    const isPageNotAccess = !publicRoutes.includes(currentPath) && !isUserLoggedIn;
    const isLoggedOut = !tokenFromStorage || !isTokenValid;
    const shouldFetchCustomer = !isLoggedOut && !profile && !publicRoutes.includes(currentPath) && !resetToken;
    const { error: fetchCustomerError, isLoading: isLoadingFetchCustomer } = useQuery(['customers'], () => fetchCustomerInfo(), {
        enabled: shouldFetchCustomer,
        onSuccess: (data) => {
            if (data) {
                dispatch({ type: SET_PROFILE, profile: data });
            } else {
                console.warn('No profile data returned');
            }
        },
        onError: () => {
            showToast();
        }
    });
    const renderMainLayout = (component) => <MainLayout isNotPublicPage={isNotPublicPage}>{component}</MainLayout>;

    const renderRoutes = () => (
        <Switch location={location} key={currentPath}>
            <Route path={ROUTES.LOGIN} component={LoginDefault} />
            <Route path={ROUTES.CONTRACT.DETAIL} component={ContractDetailDefault} />
            <Route path={ROUTES.CONTRACT.BASE} component={ContractDefault} />
            <Route path={ROUTES.CAR} component={() => <CarDefault userContractRole={userContractRole} />} />
            <Route path={ROUTES.MEMBER} component={MemberDefault} />
            <Route path={ROUTES.FORGET_PASSWORD_SENT} component={ForgetPasswordSentDefault} />
            <Route path={ROUTES.FORGET_PASSWORD} component={ForgetPasswordDefault} />
            <Route path={ROUTES.RESET_PASSWORD} component={ResetPasswordDefault} />
            <Route path={ROUTES.CHANGE_PASSWORD} component={ChangePasswordDefault} />
            <Route path={ROUTES.SET_PASSWORD} component={SetPasswordDefault} />
            <Route path={ROUTES.SET_PASSWORD_BY_TOKEN} component={SetPasswordByTokenDefault} />
            <Route path={ROUTES.PROFILE} component={ProfileDefault} />
            <Route path={ROUTES.TODAY_PRICE} component={TodayPriceDefault} />
            <Route path={ROUTES.PASSWORD_SUCCESS} component={PasswordSuccessDefault} />
            <Route path={ROUTES.CHARGING_BILL} component={ChargingBillDefault} />;
            <Route path={ROUTES.REGISTER_CARD} component={RegisterCardDefault} />;
            <Route path={ROUTES.REGISTER_CARD_SUCCESS} component={RegisterCardSuccessDefault} />;
            <Route path={ROUTES.REGISTER_CARD_FAILURE} component={RegisterCardFailureDefault} />;
            <Route path={ROUTES.CREDIT_CARD} component={CreditCardDefault} />;
            <Route path={ROUTES.EDIT_INFO} component={EditInfoDefault} />;
            <Route path={ROUTES.EDIT_EMAIL} component={EditEmailDefault} />;
            <Route path={ROUTES.EDIT_PHONE} component={EditPhoneDefault} />;
            <Route path={ROUTES.ELECTRIC.BASE} component={ElectricRoutes} />
            <Route path={ROUTES.DOCUMENT} component={DocumentDefault} />;
        </Switch>
    );

    const isPathMatched = paths.some((path) => matchPath(currentPath, { path, exact: true }));
    if (!isPathMatched) {
        return renderMainLayout(<NotFound />);
    }
    if (checkContractError) {
        return renderMainLayout(<ErrorPage />);
    }

    // Check if fetchCustomerError is due to 401 (unauthorized)
    const isUnauthorizedError = fetchCustomerError && (fetchCustomerError.statusCode === 401 || fetchCustomerError.code === 401);
    
    // Redirect to login if token is invalid, missing, or unauthorized error (except for public routes)
    if ((isPageNotAccess || isUnauthorizedError) && currentPath !== ROUTES.LOGIN) {
        // Always save return path before redirecting to login (if not already saved)
        if (!localStorage.getItem('returnPath')) {
            saveReturnPath();
        }
        return <Redirect to={ROUTES.LOGIN} />;
    }

    if (checkResetTokenError || (!resetToken && currentPath === ROUTES.RESET_PASSWORD)) {
        return renderMainLayout(<NotAccessPage />);
    }
    
    // If fetchCustomerError is not 401, show NotAccessPage
    if (fetchCustomerError && !isUnauthorizedError) {
        return renderMainLayout(<NotAccessPage />);
    }

    if (currentPath === ROUTES.ELECTRIC.EMAIL_SETTING && !profile?.is_business_account) {
        return renderMainLayout(<NotAccessPage />);
    }
    if (currentPath === ROUTES.ELECTRIC.INQUIRY && profile?.is_business_account) {
        return renderMainLayout(<NotAccessPage />);
    }
    return (
        <>
            {!isLoadingCheckContract && !isLoadingCheckResetToken && !isLoadingFetchCustomer ? (
                renderMainLayout(renderRoutes())
            ) : (
                <MainLayout />
            )}
        </>
    );
};

export default MainRoutes;
